import { Controller } from '@hotwired/stimulus';

/**
 * remarks-solved-reveal — « Afficher » / « Masquer » les remarques soldées masquées
 * d'UN cadre de la §7 du CR, à l'écran seulement, sans rien enregistrer
 * (PR 2 c-cr-lisible, D5, S2, S13, R10).
 *
 * Réglage « Remarques soldées » coupé, en rédaction : chaque cadre rend en bas ses
 * soldées masquées (`[data-solved-masked]` sur la ligne, sa ligne de détail et la
 * carte du téléphone), cachées par une règle CSS (`application.tailwind.css`) tant
 * qu'aucun ancêtre ne porte `data-solved-revealed`. Ce contrôleur pose ou retire cet
 * attribut nu sur les cibles `frame` d'UNE clé de section : le cadre de bureau ET le
 * bloc téléphone de la même entreprise, ensemble (un redimensionnement ne fait pas
 * changer d'état). Jamais l'attribut `hidden` : le filtre « Concerné / Statut » garde
 * la main sur `hidden`, indépendamment (PM3, PM4).
 *
 * L'état vit ICI, un ensemble de clés révélées, sur l'hôte NON remplacé de
 * `MeetingReports::DocumentComponent` (celui de `remarks-filter disclosure-group`) :
 * toute écriture en mode masqué remplace la section entière (R1, #555), et
 * `frameTargetConnected` / `toggleTargetConnected` réappliquent l'état aux nœuds neufs.
 *
 * Aucun texte français composé ici : le libellé visible et le nom accessible viennent
 * des data-attributs rendus SERVEUR sur le bouton (le compte a pu changer d'un rendu à
 * l'autre, le rendu neuf fait foi).
 *
 * Targets:
 *   frame  — cadre de bureau (`[data-remarks-filter-target=group]`) ou bloc téléphone
 *            (`[data-remarks-filter-target=cardGroup]`), avec `data-section-key`
 *   toggle — le `<button type=button>` de la ligne « N remarques soldées masquées »,
 *            avec `data-section-key`, `aria-expanded`, `aria-label`,
 *            `data-show-text` / `data-hide-text`, `data-show-label` / `data-hide-label`
 *
 * Actions:
 *   toggle — révèle ou recache les soldées du cadre du bouton cliqué
 *
 * @example
 *   <div data-controller="remarks-filter disclosure-group remarks-solved-reveal">
 *     <div data-remarks-filter-target="group" data-remarks-solved-reveal-target="frame"
 *          data-section-key="contractor-12">
 *       … <tr data-obs-id="7" data-solved-masked>…</tr>
 *       <button type="button" data-remarks-solved-reveal-target="toggle"
 *               data-action="remarks-solved-reveal#toggle" data-section-key="contractor-12"
 *               aria-expanded="false" aria-label="Afficher les 2 remarques soldées de X"
 *               data-show-text="Afficher" data-hide-text="Masquer"
 *               data-show-label="Afficher les 2 remarques soldées de X"
 *               data-hide-label="Masquer les 2 remarques soldées de X">Afficher</button>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ['frame', 'toggle'];

  initialize() {
    this.revealedKeys = new Set();
  }

  toggle(event) {
    const key = event.currentTarget.dataset.sectionKey;
    if (!key) return;

    if (this.revealedKeys.has(key)) this.revealedKeys.delete(key);
    else this.revealedKeys.add(key);

    this.frameTargets
      .filter((frame) => frame.dataset.sectionKey === key)
      .forEach((frame) => this.#applyFrame(frame));
    this.toggleTargets
      .filter((toggle) => toggle.dataset.sectionKey === key)
      .forEach((toggle) => this.#applyToggle(toggle));
  }

  frameTargetConnected(frame) {
    this.#applyFrame(frame);
  }

  toggleTargetConnected(toggle) {
    this.#applyToggle(toggle);
  }

  #revealed(element) {
    const key = element.dataset.sectionKey;
    return Boolean(key) && this.revealedKeys.has(key);
  }

  #applyFrame(frame) {
    frame.toggleAttribute('data-solved-revealed', this.#revealed(frame));
  }

  // Le bouton n'a pas d'icône : son `textContent` EST son libellé visible.
  #applyToggle(toggle) {
    const revealed = this.#revealed(toggle);
    const text = revealed ? toggle.dataset.hideText : toggle.dataset.showText;
    const label = revealed ? toggle.dataset.hideLabel : toggle.dataset.showLabel;
    if (text !== undefined) toggle.textContent = text;
    if (label !== undefined) toggle.setAttribute('aria-label', label);
    toggle.setAttribute('aria-expanded', String(revealed));
  }
}
